import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMsal } from '@azure/msal-react';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Drawer, Grid, IconButton, List, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { msalInstance } from '../auth';
import { isBreakpointMobile, useBreakpoint } from '../breakpoint';
import { cacheImage } from '../cache';
import { OfflineBanner } from '../components';
import { CommonContext } from '../context/CommonContext';
import { shareAbdooLink } from '../functions/shareAbdooLink';
import abdooLogo128 from '../images/Abdoo logo + safe area_128px-height.png';
import abdooLogo from '../images/Abdoo logo_64px-height.png';
import newOrbitLogo from '../images/neworbit-logo.svg';
import { AbdooLogo } from '../logos';
import { NavbarButton } from './NavbarButton';
import { NavbarLink } from './NavbarLink';
export const NavbarTemplate = ({ pageTitle, customNavbarLinks, switchPortalButton, settingsPageLink, userId, closeDrawerExternally, setCloseDrawerExternally, userEmail, }) => {
    const { isOnline, currentPage, getCachedImage } = useContext(CommonContext);
    const [renderOnInitialLoad, setRenderOnInitialLoad] = useState(false);
    const { accounts } = useMsal();
    const logoutUser = useCallback(() => {
        if (!navigator.onLine) {
            return;
        }
        localStorage.clear();
        msalInstance.logoutRedirect();
    }, []);
    const [isOpen, setIsOpen] = React.useState(false);
    const closeDrawer = useCallback(() => setIsOpen(false), []);
    const openDrawer = useCallback(() => {
        setIsOpen(true);
        setRenderOnInitialLoad(true);
    }, []);
    const breakpoint = useBreakpoint();
    const isMobile = isBreakpointMobile(breakpoint);
    const barSx = useCallback((theme) => {
        return { backgroundColor: theme.palette.background.default };
    }, []);
    useEffect(() => {
        if (closeDrawerExternally && setCloseDrawerExternally) {
            closeDrawer();
            setCloseDrawerExternally(false);
        }
    }, [closeDrawerExternally, closeDrawer, setCloseDrawerExternally]);
    const getAppVersion = useCallback(() => {
        const versionNumber = process.env.REACT_APP_VERSION;
        const lastDotIndex = versionNumber?.lastIndexOf('.');
        const removeCharAtIndex = (str, index) => str?.substring(0, index) + str?.substring(index + 1);
        return versionNumber && lastDotIndex
            ? removeCharAtIndex(versionNumber, lastDotIndex)
            : '';
    }, []);
    const share = useCallback(() => {
        shareAbdooLink(userId);
    }, [userId]);
    useEffect(() => {
        if (isOnline) {
            cacheImage(abdooLogo, 'abdooLogo64');
            cacheImage(abdooLogo128, 'abdooLogo128');
            cacheImage(newOrbitLogo, 'newOrbitLogo');
        }
    }, [isOnline]);
    if (accounts.length === 0) {
        return null;
    }
    const abdooCachedImage = getCachedImage('abdooLogo64');
    const newOrbitCachedImage = getCachedImage('newOrbitLogo');
    return (_jsx(_Fragment, { children: _jsxs(Box, { sx: barSx, width: "100%", position: "fixed", zIndex: 2, paddingY: ".3rem", paddingX: "1rem", boxShadow: "0px 10px 30px #888", borderBottom: "1px solid rgb(212, 212, 212)", mt: '-1px', id: 'navbar-template', children: [!renderOnInitialLoad && (_jsx(Box, { visibility: 'hidden', height: 0, children: _jsx("img", { height: 0, src: newOrbitLogo, alt: "NewOrbit logo" }) })), _jsxs(Grid, { container: true, children: [_jsxs(Grid, { item: true, sx: {
                                ...(currentPage === 'calendar' &&
                                    !isOnline &&
                                    isMobile && {
                                    display: 'flex',
                                    alignItems: 'center',
                                }),
                            }, xs: isOnline ? 12 : 6, md: 4, children: [_jsx(IconButton, { onClick: openDrawer, color: "primary", "data-testid": "navbar-open-button", children: _jsx(MenuIcon, {}) }), _jsx(Typography, { component: "span", color: "primary", fontWeight: "bold", marginLeft: "1em", children: pageTitle }), isMobile && !isOnline && currentPage === 'calendar' && (_jsx(OfflineBanner, { dataTestId: 'top-bar-offline-banner', isOffline: !isOnline, sx: { padding: '4px 0', marginLeft: '1.5rem', width: '20%' } }))] }), isMobile ? null : (_jsx(Grid, { item: true, container: true, md: 4, justifyContent: "center", alignContent: "center", alignItems: "center", justifyItems: "center", children: _jsx(AbdooLogo, { src: abdooLogo, sx: {
                                    height: '2.25em',
                                    display: 'block',
                                } }) })), currentPage !== 'calendar' && (_jsx(Grid, { item: true, xs: 6, md: 4, alignSelf: 'center', children: _jsx(OfflineBanner, { dataTestId: 'top-bar-offline-banner', isOffline: !isOnline, sx: { p: '4px 0', width: '50%', ml: 'auto' } }) }))] }), _jsx(Drawer, { anchor: 'left', open: isOpen, onClose: closeDrawer, children: _jsxs(Box, { sx: {
                            justifyContent: 'space-between',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            width: '300px',
                        }, role: "presentation", children: [_jsxs("section", { children: [_jsx(OfflineBanner, { dataTestId: 'nav-bar-offline-banner', isOffline: !isOnline, sx: { width: '100%', p: '14px 0' } }), _jsx(Box, { sx: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                            padding: '16px 0px',
                                            marginBottom: '8px',
                                        }, children: _jsx(AbdooLogo, { src: isOnline ? abdooLogo : abdooCachedImage, sx: { height: '2.25em' } }) }), switchPortalButton, _jsxs(List, { onClick: closeDrawer, children: [customNavbarLinks, _jsx(Box, { sx: {
                                                    margin: '8px 16px',
                                                    height: '1px',
                                                    border: 'none',
                                                    color: '#c8cdd0',
                                                    backgroundColor: '#c8cdd0',
                                                } }), _jsx(NavbarButton, { buttonKey: "share", onClick: share, text: "Share", children: _jsx(ShareIcon, {}) }), _jsx(NavbarLink, { offlineMode: !isOnline, linkTo: settingsPageLink, linkKey: "settings", text: "Settings", children: _jsx(SettingsIcon, {}) }), _jsx(NavbarButton, { offlineMode: !isOnline, buttonKey: "logout", onClick: logoutUser, text: "Logout", children: _jsx(ExitToAppIcon, {}) })] })] }), userEmail && (_jsx(Box, { display: 'flex', flexDirection: 'row', alignItems: 'flex-end', height: '100%', padding: '0 1rem', justifyContent: 'center', fontSize: '0.9rem', children: userEmail })), _jsxs(Box, { component: "footer", children: [_jsx("img", { src: isOnline ? newOrbitLogo : newOrbitCachedImage, alt: "NewOrbit logo" }), _jsxs(Box, { sx: {
                                            fontSize: 12,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            marginRight: '5px',
                                        }, children: ["v. ", getAppVersion()] })] })] }) })] }) }));
};
